import React, { useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useRecoilValue, useResetRecoilState } from 'recoil';
import PropTypes from 'prop-types';
import styled, { useTheme } from 'styled-components';

import { clearAccessTokens } from '@ecp/common/src/utils/utils';
import { useLogin } from '@ecp/common/src/hooks/useLoginForward';
import { FlexBox, FlexBoxColumn } from '@ecp/common/src/layouts/flex/styled';
import { NoneDecorationLink, StyledTextLink } from '@ecp/common/src/text/TextLink';
import { TextBox } from '@ecp/common/src/text/TextBox';
import ContextMenuItem from '@ecp/common/src/components/contextmenu/ContextMenuItem';

import { withCartBadgeCount } from '@fo-recoil/order/selector';
import { myDeliveryPlaceListState } from '@fo-recoil/order/atom';
import { userInfoState } from '@fo-recoil/common/user/atom';
import { useConfirmDialog } from '@fo-hooks/common/useModal';
import useSiteInfo from '@fo-hooks/common/useSiteInfo';
import authApi from '@fo-apis/common/authApi';
import GnbIconButton from '@fo-pages/home/gnb/GnbIconButton';
import { ReactComponent as MyPageIcon } from '@fo-assets/icon/skypass/icon_mypage.svg';
import { ReactComponent as CartIcon } from '@fo-assets/icon/skypass/icon_cart.svg';
import { ReactComponent as CustomerServiceIcon } from '@fo-assets/icon/skypass/icon_inquiry.svg';
import MileageMallIcon from '@fo-assets/icon/icon_mileage-mall.svg';
import { ReactComponent as HomeIcon } from '@fo-assets/icon/skypass/icon_home.svg';
import { ReactComponent as LogoutIcon } from '@fo-assets/icon/skypass/icon_logout.svg';
import { Wrapper, Logo, ShortcutButton, LoginButton } from '@ecp/fo/sites/skypass/styles/gnb/SkypassGnbHeaderStyles';
import GnbSearch from '@ecp/fo/src/pages/home/gnb/search/GnbSearch';

const GNB_LINK_URLS = {
  MY_PAGE: '/my-page',
  MEMBER: '/member',
  ORDER: '/my-page/order',
  CART: 'cart',
  COUPON: '/my-page/coupon',
  POINT: '/my-page/asset-point',
  CUSTOMER_SERVICE: '/customer-service/qna', //대한항공은 faq가 없으므로 자주 찾는 질문 top5로 랜딩 되지 않고 바로 1:1문의함으로 랜딩.
  GUEST_CUSTOMER_SERVICE: '/customer-service/notice', // 대한한공 비회원은 faq가 없으므로 공지사항으로 랜딩.
};

const SkypassGnbHeader = React.forwardRef(({ searchPhraseList, searchPraseListIndex }, ref) => {
  const theme = useTheme();
  const { showConfirmDialog } = useConfirmDialog();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const {
    isCartUse,
    systemInformation: { pcGnbImgFilePath },
    siteNumber,
  } = useSiteInfo();

  const cartBadgeCount = useRecoilValue(withCartBadgeCount);
  const resetDeliveryPlaceList = useResetRecoilState(myDeliveryPlaceListState);
  const { login } = useRecoilValue(userInfoState);
  const loginForward = useLogin(siteNumber);
  const logout = useCallback(async () => {
    await authApi.logout();
    clearAccessTokens();
    resetDeliveryPlaceList();
    navigate('/');
  }, [navigate, resetDeliveryPlaceList]);

  const onClickLogout = useCallback(() => {
    showConfirmDialog('로그아웃 하시겠습니까?', logout);
  }, [logout, showConfirmDialog]);

  const { isHome } = useMemo(
    () => ({
      isHome: pathname === '/',
    }),
    [pathname]
  );

  function onClickShortcut() {
    window.open('https://www.koreanair.com/contents/skypass/mileage-mall', '_blank');
  }

  const handleLoginClick = () => {
    if (!login) {
      loginForward();
    }
  };

  const handleMoveClick = (to) => {
    if (
      !login &&
      !['/', '/customer-service/notice', '/customer-service/faq', 'customer-service', '/customer-service'].includes(to)
    ) {
      loginForward();
    } else {
      navigate(`${to}`);
    }
  };

  return (
    <Wrapper ref={ref}>
      <FlexBox>
        <NoneDecorationLink to={''}>
          <Logo data-ds-label2={'g_logo'} src={pcGnbImgFilePath} />
        </NoneDecorationLink>
        <GnbSearch searchPhraseList={searchPhraseList} searchPraseListIndex={searchPraseListIndex} />
      </FlexBox>
      <FlexBox gap={'20px'}>
        <ShortcutButton onClick={onClickShortcut}>
          <TextBox>대한항공 마일리지 몰 바로가기</TextBox>
          <img src={MileageMallIcon} alt={'shortcut'} />
        </ShortcutButton>
        {!login && <LoginButton onClick={handleLoginClick}>로그인</LoginButton>}
        <FlexBox gap={'15px'}>
          {!isHome && (
            <GnbButton
              onClick={() => {
                handleMoveClick('/');
              }}
            >
              <HomeIcon />
              <Label color={theme.color.text.basic}>홈화면</Label>
            </GnbButton>
          )}

          <GnbButton
            onClick={() => {
              handleMoveClick(GNB_LINK_URLS.MY_PAGE);
            }}
          >
            <MyPageIcon />
            <Label color={theme.color.text.basic}>마이페이지</Label>
          </GnbButton>

          {isCartUse && (
            <GnbIconButton
              showZero
              iconType={CartIcon}
              badgeContent={cartBadgeCount}
              label={'장바구니'}
              to={GNB_LINK_URLS.CART}
              data-ds-label2={'g_basket'}
              useDefaultColor={true}
            />
          )}

          <GnbButton
            onClick={() => {
              handleMoveClick(login ? GNB_LINK_URLS.CUSTOMER_SERVICE : GNB_LINK_URLS.GUEST_CUSTOMER_SERVICE);
            }}
          >
            <CustomerServiceIcon />
            <Label color={theme.color.text.basic}>고객센터</Label>
          </GnbButton>

          {login && (
            <GnbButton
              onClick={() => {
                onClickLogout();
              }}
            >
              <LogoutIcon />
              <Label color={theme.color.text.basic}>로그아웃</Label>
            </GnbButton>
          )}
        </FlexBox>
      </FlexBox>
    </Wrapper>
  );
});

SkypassGnbHeader.displayName = 'SkypassGnbHeader';
export default SkypassGnbHeader;

SkypassGnbHeader.propTypes = {
  searchPhraseList: PropTypes.array,
  searchPraseListIndex: PropTypes.number,
};

function MyPageMenuItems(props) {
  const { clickWhenLogout: handleLogoutClick } = props;
  return (
    <FlexBoxColumn gap="6px" width="100%" align-items="flex-start">
      <ContextMenuItem size={'13px'} to="/my-page">
        마이페이지
      </ContextMenuItem>
      <ContextMenuItem size={'13px'} to="/my-page/edit">
        내정보수정
      </ContextMenuItem>
      <StyledTextLink size={'13px'} decorationOnHover onClick={handleLogoutClick}>
        로그아웃
      </StyledTextLink>
    </FlexBoxColumn>
  );
}

MyPageMenuItems.propTypes = {
  clickWhenLogout: PropTypes.func,
};

const Label = styled.span.attrs(({ color, style }) => ({ style: { ...style, color } }))`
  font-size: 11px;
  line-height: 11px;
  font-weight: ${(props) => props.theme.font.weight.demiLight};
`;

const GnbButton = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 3px;
  cursor: pointer;
`;
